import React, { useState, useMemo } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { 
    ArrowLeft, 
    TrendingUp, 
    DollarSign, 
    Home, 
    Users, 
    Calendar, 
    Download, 
    Filter,
    BarChart3,
    PieChart,
    Activity,
    ArrowUpRight,
    ArrowDownRight,
    Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';

interface PropertyPerformanceData {
    propertyId: string;
    propertyName: string;
    totalRevenue: number;
    occupancyRate: number;
    avgRentPerUnit: number;
    maintenanceCosts: number;
    netIncome: number;
    roi: number;
    tenantTurnoverRate: number;
    avgDaysToRent: number;
    expenses: {
        category: string;
        amount: number;
        percentage: number;
    }[];
    monthlyRevenue: {
        month: string;
        revenue: number;
        expenses: number;
    }[];
}

export const PropertyPerformance: React.FC = () => {
    const navigate = useNavigate();
    const { success } = useToast();
    const { properties, invoices, units, loading } = useLandlordStore();
    const [selectedProperty, setSelectedProperty] = useState<string>('all');
    const [dateRange, setDateRange] = useState<string>('6months');
    const [activeTab, setActiveTab] = useState<'overview' | 'revenue' | 'expenses'>('overview');

    // Mock performance data - in real app, this would be calculated from actual data
    const mockPerformanceData: PropertyPerformanceData[] = [
        {
            propertyId: 'prop_1',
            propertyName: 'Sunrise Apartments',
            totalRevenue: 1250000,
            occupancyRate: 92,
            avgRentPerUnit: 45000,
            maintenanceCosts: 125000,
            netIncome: 985000,
            roi: 12.5,
            tenantTurnoverRate: 8,
            avgDaysToRent: 14,
            expenses: [
                { category: 'Maintenance', amount: 125000, percentage: 25 },
                { category: 'Utilities', amount: 75000, percentage: 15 },
                { category: 'Insurance', amount: 50000, percentage: 10 },
                { category: 'Property Tax', amount: 100000, percentage: 20 },
                { category: 'Management', amount: 150000, percentage: 30 }
            ],
            monthlyRevenue: [
                { month: 'Jan', revenue: 180000, expenses: 45000 },
                { month: 'Feb', revenue: 185000, expenses: 42000 },
                { month: 'Mar', revenue: 190000, expenses: 48000 },
                { month: 'Apr', revenue: 195000, expenses: 50000 },
                { month: 'May', revenue: 200000, expenses: 55000 },
                { month: 'Jun', revenue: 210000, expenses: 48000 }
            ]
        },
        {
            propertyId: 'prop_2',
            propertyName: 'Majengo Heights',
            totalRevenue: 980000,
            occupancyRate: 85,
            avgRentPerUnit: 38000,
            maintenanceCosts: 98000,
            netIncome: 752000,
            roi: 10.2,
            tenantTurnoverRate: 12,
            avgDaysToRent: 21,
            expenses: [
                { category: 'Maintenance', amount: 98000, percentage: 22 },
                { category: 'Utilities', amount: 65000, percentage: 14 },
                { category: 'Insurance', amount: 42000, percentage: 9 },
                { category: 'Property Tax', amount: 85000, percentage: 19 },
                { category: 'Management', amount: 158000, percentage: 36 }
            ],
            monthlyRevenue: [
                { month: 'Jan', revenue: 150000, expenses: 38000 },
                { month: 'Feb', revenue: 155000, expenses: 35000 },
                { month: 'Mar', revenue: 160000, expenses: 40000 },
                { month: 'Apr', revenue: 165000, expenses: 42000 },
                { month: 'May', revenue: 170000, expenses: 45000 },
                { month: 'Jun', revenue: 175000, expenses: 38000 }
            ]
        }
    ];

    const filteredData = useMemo(() => {
        if (selectedProperty === 'all') {
            return mockPerformanceData;
        }
        return mockPerformanceData.filter(p => p.propertyId === selectedProperty);
    }, [selectedProperty]);

    const aggregateData = useMemo(() => {
        if (filteredData.length === 0) return null;
        
        const totalRevenue = filteredData.reduce((sum, p) => sum + p.totalRevenue, 0);
        const totalNetIncome = filteredData.reduce((sum, p) => sum + p.netIncome, 0);
        const avgOccupancy = filteredData.reduce((sum, p) => sum + p.occupancyRate, 0) / filteredData.length;
        const avgRoi = filteredData.reduce((sum, p) => sum + p.roi, 0) / filteredData.length;
        
        return { totalRevenue, totalNetIncome, avgOccupancy, avgRoi };
    }, [filteredData]);

    const handleDownloadReport = () => {
        success('Download Started', 'Property performance report is being generated...');
        // In real app, this would trigger a CSV/PDF download
    };

    const formatCurrency = (value: number) => {
        return `KES ${value.toLocaleString()}`;
    };

    const getTrendIcon = (value: number, positiveIsGood: boolean = true) => {
        const isPositive = positiveIsGood ? value >= 0 : value <= 0;
        return isPositive ? (
            <ArrowUpRight size={16} className="text-green-600 dark:text-green-400" />
        ) : (
            <ArrowDownRight size={16} className="text-red-600 dark:text-red-400" />
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="animate-spin text-primary w-10 h-10" />
                <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading performance data...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12 min-h-[80vh]">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 border-b border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <div className="flex-1">
                    <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Property Performance</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Analytics and insights for your property portfolio</p>
                </div>
                <button
                    onClick={handleDownloadReport}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    <Download size={18} />
                    Export Report
                </button>
            </div>

            {/* Filters */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border px-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Property Filter */}
                    <div className="md:w-64">
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                            Select Property
                        </label>
                        <select
                            value={selectedProperty}
                            onChange={(e) => setSelectedProperty(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text transition-all"
                        >
                            <option value="all">All Properties</option>
                            {properties.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date Range Filter */}
                    <div className="md:w-48">
                        <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                            Date Range
                        </label>
                        <select
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text transition-all"
                        >
                            <option value="1month">Last Month</option>
                            <option value="3months">Last 3 Months</option>
                            <option value="6months">Last 6 Months</option>
                            <option value="1year">Last Year</option>
                            <option value="all">All Time</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            {aggregateData && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-4">
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Total Revenue</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">
                                    {formatCurrency(aggregateData.totalRevenue)}
                                </h3>
                                <div className="flex items-center gap-1 mt-2 text-sm">
                                    {getTrendIcon(8.5)}
                                    <span className="text-green-600 dark:text-green-400">+8.5%</span>
                                    <span className="text-light-subtle dark:text-dark-subtle">vs last period</span>
                                </div>
                            </div>
                            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                <DollarSign size={24} className="text-green-600 dark:text-green-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Net Income</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">
                                    {formatCurrency(aggregateData.totalNetIncome)}
                                </h3>
                                <div className="flex items-center gap-1 mt-2 text-sm">
                                    {getTrendIcon(12.3)}
                                    <span className="text-green-600 dark:text-green-400">+12.3%</span>
                                    <span className="text-light-subtle dark:text-dark-subtle">vs last period</span>
                                </div>
                            </div>
                            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <TrendingUp size={24} className="text-blue-600 dark:text-blue-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Avg Occupancy</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">
                                    {aggregateData.avgOccupancy.toFixed(1)}%
                                </h3>
                                <div className="flex items-center gap-1 mt-2 text-sm">
                                    {getTrendIcon(3.2)}
                                    <span className="text-green-600 dark:text-green-400">+3.2%</span>
                                    <span className="text-light-subtle dark:text-dark-subtle">vs last period</span>
                                </div>
                            </div>
                            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                                <Home size={24} className="text-purple-600 dark:text-purple-400" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm text-light-subtle dark:text-dark-subtle">Avg ROI</p>
                                <h3 className="text-2xl font-bold text-light-text dark:text-dark-text mt-1">
                                    {aggregateData.avgRoi.toFixed(1)}%
                                </h3>
                                <div className="flex items-center gap-1 mt-2 text-sm">
                                    {getTrendIcon(-1.5, false)}
                                    <span className="text-red-600 dark:text-red-400">-1.5%</span>
                                    <span className="text-light-subtle dark:text-dark-subtle">vs last period</span>
                                </div>
                            </div>
                            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                <Activity size={24} className="text-orange-600 dark:text-orange-400" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border px-4">
                <div className="flex border-b border-light-border dark:border-dark-border">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                            activeTab === 'overview'
                                ? 'text-primary border-primary'
                                : 'text-light-subtle dark:text-dark-subtle border-transparent hover:text-light-text dark:hover:text-dark-text'
                        }`}
                    >
                        <span className="flex items-center gap-2">
                            <BarChart3 size={16} />
                            Overview
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('revenue')}
                        className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                            activeTab === 'revenue'
                                ? 'text-primary border-primary'
                                : 'text-light-subtle dark:text-dark-subtle border-transparent hover:text-light-text dark:hover:text-dark-text'
                        }`}
                    >
                        <span className="flex items-center gap-2">
                            <TrendingUp size={16} />
                            Revenue
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('expenses')}
                        className={`px-6 py-4 text-sm font-medium transition-colors border-b-2 ${
                            activeTab === 'expenses'
                                ? 'text-primary border-primary'
                                : 'text-light-subtle dark:text-dark-subtle border-transparent hover:text-light-text dark:hover:text-dark-text'
                        }`}
                    >
                        <span className="flex items-center gap-2">
                            <PieChart size={16} />
                            Expenses
                        </span>
                    </button>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            {/* Property Performance Table */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-light-border dark:border-dark-border">
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Property</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Revenue</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Net Income</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Occupancy</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">ROI</th>
                                            <th className="pb-4 text-sm font-medium text-light-subtle dark:text-dark-subtle">Turnover</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-light-border dark:divide-dark-border">
                                        {filteredData.map(property => (
                                            <tr key={property.propertyId} className="hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
                                                <td className="py-4">
                                                    <span className="font-medium text-light-text dark:text-dark-text">{property.propertyName}</span>
                                                </td>
                                                <td className="py-4 text-light-text dark:text-dark-text">{formatCurrency(property.totalRevenue)}</td>
                                                <td className="py-4 text-light-text dark:text-dark-text">{formatCurrency(property.netIncome)}</td>
                                                <td className="py-4">
                                                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                                                        property.occupancyRate >= 90
                                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                            : property.occupancyRate >= 75
                                                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                    }`}>
                                                        {property.occupancyRate}%
                                                    </span>
                                                </td>
                                                <td className="py-4 text-light-text dark:text-dark-text">{property.roi}%</td>
                                                <td className="py-4 text-light-text dark:text-dark-text">{property.tenantTurnoverRate}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Monthly Revenue Chart Placeholder */}
                            <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-6">
                                <h4 className="text-sm font-medium text-light-text dark:text-dark-text mb-4">Revenue Trend (Last 6 Months)</h4>
                                <div className="h-64 flex items-end justify-between gap-2">
                                    {filteredData[0]?.monthlyRevenue.map((data, index) => (
                                        <div key={index} className="flex-1 flex flex-col items-center gap-2">
                                            <div className="w-full bg-primary/20 rounded-t-lg relative" style={{ height: `${(data.revenue / 250000) * 100}%` }}>
                                                <div className="absolute bottom-0 w-full bg-primary rounded-t-lg" style={{ height: `${(data.revenue - data.expenses) / data.revenue * 100}%` }}></div>
                                            </div>
                                            <span className="text-xs text-light-subtle dark:text-dark-subtle">{data.month}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center justify-center gap-6 mt-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-primary/20 rounded"></div>
                                        <span className="text-xs text-light-subtle dark:text-dark-subtle">Revenue</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-primary rounded"></div>
                                        <span className="text-xs text-light-subtle dark:text-dark-subtle">Net</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'revenue' && (
                        <div className="space-y-6">
                            {filteredData.map(property => (
                                <div key={property.propertyId} className="space-y-4">
                                    <h4 className="text-lg font-bold text-light-text dark:text-dark-text">{property.propertyName}</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Total Revenue</p>
                                            <p className="text-xl font-bold text-light-text dark:text-dark-text">{formatCurrency(property.totalRevenue)}</p>
                                        </div>
                                        <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Avg Rent/Unit</p>
                                            <p className="text-xl font-bold text-light-text dark:text-dark-text">{formatCurrency(property.avgRentPerUnit)}</p>
                                        </div>
                                        <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Avg Days to Rent</p>
                                            <p className="text-xl font-bold text-light-text dark:text-dark-text">{property.avgDaysToRent} days</p>
                                        </div>
                                        <div className="bg-light-bg dark:bg-dark-bg rounded-lg p-4">
                                            <p className="text-sm text-light-subtle dark:text-dark-subtle">Turnover Rate</p>
                                            <p className="text-xl font-bold text-light-text dark:text-dark-text">{property.tenantTurnoverRate}%</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeTab === 'expenses' && (
                        <div className="space-y-6">
                            {filteredData.map(property => (
                                <div key={property.propertyId} className="space-y-4">
                                    <h4 className="text-lg font-bold text-light-text dark:text-dark-text">{property.propertyName}</h4>
                                    <div className="space-y-3">
                                        {property.expenses.map((expense, index) => (
                                            <div key={index} className="flex items-center gap-4">
                                                <div className="w-32 text-sm text-light-text dark:text-dark-text">{expense.category}</div>
                                                <div className="flex-1 h-8 bg-light-bg dark:bg-dark-bg rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-primary rounded-full" 
                                                        style={{ width: `${expense.percentage}%` }}
                                                    ></div>
                                                </div>
                                                <div className="w-24 text-right text-sm font-medium text-light-text dark:text-dark-text">
                                                    {formatCurrency(expense.amount)}
                                                </div>
                                                <div className="w-16 text-right text-sm text-light-subtle dark:text-dark-subtle">
                                                    {expense.percentage}%
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="pt-4 border-t border-light-border dark:border-dark-border">
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-light-text dark:text-dark-text">Total Expenses</span>
                                            <span className="text-xl font-bold text-light-text dark:text-dark-text">
                                                {formatCurrency(property.maintenanceCosts + property.expenses.reduce((sum, e) => sum + e.amount, 0) - property.maintenanceCosts)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};